import { SERVICE_TYPES } from '@/constants/serviceTypes';
import { dimensionLabel } from '@/components/Account/ServiceCatalog/utils';
import { getOrderCatalogScope } from '../../../utilities/serviceCatalog';
import { isServiceLabel } from '../../../utilities/serviceLines';

// Teammate roles that may quick add (useClientUser expands an admin's roles to all of them).
const QUICK_ADD_TEAMMATE_ROLES = ['admin', 'operations'];

// Quick add writes the shipper's catalog on the spot, and Hasura doesn't limit whose catalog a user
// writes, so this is the gate: the catalog's own shipper account (an Onward admin impersonating it
// included) or its admin / operations teammates — wider than the Account page, which gives the
// catalog to admin teammates only. A teammate is told apart by userType, not by having roles, so
// one with no roles saved doesn't pass as the account owner. Not on a read-only order, where
// nothing else saves.
export const canQuickAddService = ({ accountType, clientId, shipperId, readOnly, userType, teammateRoles } = {}) =>
    !readOnly &&
    accountType === 'shipper' &&
    !!shipperId &&
    clientId === shipperId &&
    (userType !== 'teammate' || QUICK_ADD_TEAMMATE_ROLES.some((role) => !!teammateRoles?.[role]));

// Defaults for adding a service line's code to the catalog, catalog-row-shaped for
// ServiceCatalogModal. The type carries over only when the catalog offers it, so the parser's
// 'unknown' stays blank. Scoped to the order's own scope, which the modal locks to Non Billable
// unless the shipper has catalog service rates (see scopedNonBillableApplies).
export const quickAddCatalogDefaults = (group, order) => ({
    service_code: `${group?.code ?? ''}`.trim(),
    description: isServiceLabel(group?.description, group?.code) ? `${group.description}`.trim() : '',
    service_type: SERVICE_TYPES.includes(group?.type) ? group.type : '',
    size: group?.size || '',
    service_time: Number.isFinite(group?.service_time) ? group.service_time : null,
    dimensions: getOrderCatalogScope(order),
});

// Scope pickers for a quick add: each of the order's own dimensions, offering only its value (None
// widens the entry to every scope). Either way the new entry is one this order's catalog query
// returns, so the line it was added for validates.
export const quickAddDimensionFields = (order) =>
    Object.entries(getOrderCatalogScope(order))
        .map(([key, value]) => [key, `${value ?? ''}`.trim()])
        .filter(([key, value]) => key && value)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([key, value]) => ({ key, label: dimensionLabel(key), values: [value] }));
